import React from 'react';
import { Box, Container, Typography, Paper, List, ListItem, ListItemText, ListItemIcon, Divider, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginIcon from '@mui/icons-material/Login';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';

const PublicPrivacy: React.FC = () => {
  const { currentUser } = useAuth();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {!currentUser && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'primary.dark' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" color="white">
              Sign in to manage your privacy settings and data
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              color="secondary"
              startIcon={<LoginIcon />}
              sx={{ 
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'secondary.light'
                }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      )}

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        
        <Typography variant="body1" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Data Collection Practices
          </Typography>
          <Typography variant="body1" paragraph>
            We collect and process the following types of data:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Personal Information"
                secondary="Email address, name, and account preferences"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Usage Data"
                secondary="App usage patterns, feature interactions, and performance metrics"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Device Information"
                secondary="Device type, operating system, and browser information"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Data Usage Policies
          </Typography>
          <Typography variant="body1" paragraph>
            We use your data to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Service Provision"
                secondary="Provide and maintain our services"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Improvement"
                secondary="Enhance and optimize our services"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Communication"
                secondary="Send important updates and notifications"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            User Rights and Controls
          </Typography>
          <Typography variant="body1" paragraph>
            You have the right to:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Access"
                secondary="Request access to your personal data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Correction"
                secondary="Request correction of inaccurate data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DeleteForeverIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Deletion"
                secondary="Request deletion of your data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Objection"
                secondary="Object to data processing"
              />
            </ListItem>
          </List>
          
          <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/data-deletion-request"
              startIcon={<DeleteForeverIcon />}
              sx={{ flex: 1 }}
            >
              Request Data Deletion
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              component={RouterLink}
              to="/login"
              startIcon={<PersonIcon />}
              sx={{ flex: 1 }}
            >
              Sign In to Manage Data
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can request data deletion without an account, or log in to your account to manage your data directly through your profile settings.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            GDPR Compliance
          </Typography>
          <Typography variant="body1" paragraph>
            We comply with the General Data Protection Regulation (GDPR) and ensure:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Lawful Processing"
                secondary="All data processing is done with a legal basis"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Minimization"
                secondary="Only necessary data is collected"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Security"
                secondary="Appropriate security measures are in place"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            CCPA Compliance
          </Typography>
          <Typography variant="body1" paragraph>
            For California residents, we comply with the California Consumer Privacy Act (CCPA) and provide:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Right to Know"
                secondary="Information about data collection and usage"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Delete"
                secondary="Ability to request data deletion"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Opt-Out"
                secondary="Option to opt-out of data sales"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            For privacy-related inquiries, please contact us at:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Email"
                secondary="privacy@kubomaster.llc"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Address"
                secondary="[Your Company Address]"
              />
            </ListItem>
          </List>
        </Box>
      </Paper>
    </Container>
  );
};

export default PublicPrivacy; 